import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, UserPlus, Users, Trash2, RotateCw, MailWarning, CheckCircle2, Copy, CheckCheck } from "lucide-react";

/**
 * One-stop user directory per client.
 *   • Active users (with last-login)
 *   • Pending invites (resend / cancel)
 *   • Invite form
 */
export default function ClientUsersModal({ open, clientId, clientName, currentUserId, onClose }) {
  const [data, setData] = useState({ active: [], pending: [] });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [copied, setCopied] = useState(null);
  const [tab, setTab] = useState("active");
  const [allClients, setAllClients] = useState([]);
  const [form, setForm] = useState({ name: "", email: "" });
  const [extraClientIds, setExtraClientIds] = useState([]); // additional clients (multi-tenant)
  const [inviting, setInviting] = useState(false);
  const [freshUrl, setFreshUrl] = useState(null);

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/auth/users?client_id=${encodeURIComponent(clientId)}`);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load users");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (open) {
      load();
      api.get("/clients").then((r) => setAllClients(r.data)).catch(() => {});
      setExtraClientIds([]);
    }
  }, [open, clientId]);

  if (!open) return null;

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const client_ids = [clientId, ...extraClientIds];
      const { data: d } = await api.post("/auth/invite", { ...form, role: "client", client_ids });
      toast.success(`Invite sent to ${form.email}`);
      setFreshUrl(d.invite_url);
      setForm({ name: "", email: "" });
      setExtraClientIds([]);
      setTab("pending");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invite");
    } finally { setInviting(false); }
  };

  const toggleExtra = (id) => {
    setExtraClientIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const resend = async (row) => {
    setBusyId(row.user_id);
    try {
      const { data: d } = await api.post(`/auth/resend-invite/${row.user_id}`);
      toast.success(`Invite resent to ${row.email}`);
      setFreshUrl(d.invite_url);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally { setBusyId(null); }
  };

  const cancelInvite = async (row) => {
    if (!window.confirm(`Cancel invite for ${row.email}?`)) return;
    setBusyId(row.user_id);
    try {
      await api.delete(`/auth/invite/${row.user_id}`);
      toast.success("Invite cancelled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    } finally { setBusyId(null); }
  };

  const removeActive = async (row) => {
    if (row.user_id === currentUserId) { toast.error("You cannot remove your own account."); return; }
    if (!window.confirm(`Remove ${row.email}? They will lose access immediately.`)) return;
    setBusyId(row.user_id);
    try {
      await api.delete(`/auth/users/${row.user_id}`);
      toast.success(`Removed ${row.email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    } finally { setBusyId(null); }
  };

  const copyUrl = async () => {
    if (!freshUrl) return;
    try {
      await navigator.clipboard.writeText(freshUrl);
      setCopied(freshUrl);
      setTimeout(() => setCopied(null), 2000);
    } catch { toast.error("Copy failed"); }
  };

  const relTime = (iso) => {
    if (!iso) return "Never";
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="hbt-card w-full max-w-3xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="client-users-modal">
        <div className="p-6 border-b flex items-start justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="hbt-label flex items-center gap-2"><Users className="w-3 h-3" /> User directory</div>
            <div className="font-display text-2xl font-semibold mt-1">{clientName}</div>
            <div className="text-xs text-neutral-500 mt-1">
              <span data-testid="active-users-count">{data.active.length} active</span> ·
              <span data-testid="pending-users-count"> {data.pending.length} pending</span>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-black" data-testid="client-users-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="border-b flex" style={{ borderColor: "var(--border)" }} data-testid="client-users-tabs">
          {[
            { k: "active", label: `Active (${data.active.length})` },
            { k: "pending", label: `Pending (${data.pending.length})` },
            { k: "invite", label: "Invite" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-6 py-3 text-sm font-medium transition-colors ${tab === t.k ? "border-b-2" : "text-neutral-500 hover:text-black"}`}
              style={{ borderColor: tab === t.k ? "var(--forest)" : "transparent", color: tab === t.k ? "var(--forest)" : undefined }}
              data-testid={`client-users-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {freshUrl && (
            <div className="p-4 mx-6 mt-4 rounded-md text-xs flex items-center gap-3" style={{ background: "rgba(26,54,45,0.06)", color: "var(--forest)" }} data-testid="fresh-invite-banner">
              <MailWarning className="w-4 h-4 shrink-0" />
              <div className="flex-1 min-w-0 truncate font-mono">{freshUrl}</div>
              <button onClick={copyUrl} className="btn-outline text-xs inline-flex items-center gap-1 shrink-0" data-testid="copy-fresh-url">
                {copied ? <CheckCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
          )}

          {loading && <div className="text-center text-neutral-400 py-12">Loading…</div>}

          {!loading && tab === "active" && (
            <div className="p-6">
              {data.active.length === 0
                ? <div className="text-center text-neutral-400 py-10">No active users yet. Invite one below.</div>
                : (
                  <table className="hbt-table">
                    <thead><tr><th>Person</th><th>Role</th><th>Last login</th><th className="text-right pr-2">Actions</th></tr></thead>
                    <tbody>
                      {data.active.map((u) => (
                        <tr key={u.user_id} data-testid={`active-user-row-${u.user_id}`}>
                          <td>
                            <div className="font-medium flex items-center gap-2">
                              {u.name || "—"}
                              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--forest)" }} />
                            </div>
                            <div className="text-xs text-neutral-500">{u.email}</div>
                          </td>
                          <td className="text-xs capitalize">{u.role}</td>
                          <td className="text-xs text-neutral-500" title={u.last_login_at || ""} data-testid={`last-login-${u.user_id}`}>{relTime(u.last_login_at)}</td>
                          <td className="text-right pr-2">
                            <button
                              onClick={() => removeActive(u)}
                              disabled={busyId === u.user_id || u.user_id === currentUserId}
                              className="btn-outline text-xs inline-flex items-center gap-1"
                              data-testid={`remove-user-${u.user_id}`}
                              style={{ color: "var(--terracotta,#C85A3C)" }}
                              title={u.user_id === currentUserId ? "You can't remove yourself" : "Remove user"}
                            >
                              <Trash2 className="w-3 h-3" /> Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          )}

          {!loading && tab === "pending" && (
            <div className="p-6">
              {data.pending.length === 0
                ? <div className="text-center text-neutral-400 py-10">Nobody is waiting to activate. All good.</div>
                : (
                  <table className="hbt-table">
                    <thead><tr><th>Person</th><th>Last sent</th><th>Status</th><th className="text-right pr-2">Actions</th></tr></thead>
                    <tbody>
                      {data.pending.map((u) => (
                        <tr key={u.user_id} data-testid={`pending-user-row-${u.user_id}`}>
                          <td>
                            <div className="font-medium">{u.name || "—"}</div>
                            <div className="text-xs text-neutral-500">{u.email}</div>
                          </td>
                          <td className="text-xs text-neutral-500">{relTime(u.last_sent_at)}</td>
                          <td>
                            {u.status === "expired"
                              ? <span className="pill" style={{ background: "rgba(200,90,60,0.15)", color: "var(--terracotta,#C85A3C)" }}>Expired</span>
                              : <span className="pill forest">Pending</span>}
                          </td>
                          <td className="text-right pr-2 space-x-1 whitespace-nowrap">
                            <button onClick={() => resend(u)} disabled={busyId === u.user_id} className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`resend-user-${u.user_id}`}>
                              <RotateCw className={`w-3 h-3 ${busyId === u.user_id ? "animate-spin" : ""}`} /> Resend
                            </button>
                            <button onClick={() => cancelInvite(u)} disabled={busyId === u.user_id} className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`cancel-user-${u.user_id}`} style={{ color: "var(--terracotta,#C85A3C)" }}>
                              <Trash2 className="w-3 h-3" /> Cancel
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          )}

          {!loading && tab === "invite" && (
            <form onSubmit={invite} className="p-6 space-y-4 max-w-lg" data-testid="invite-tab-form">
              <div>
                <label className="hbt-label block mb-2">Full name</label>
                <input required className="hbt-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Thandi Dlamini" data-testid="invite-tab-name" />
              </div>
              <div>
                <label className="hbt-label block mb-2">Work email</label>
                <input required type="email" className="hbt-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="sustainability@client.co.sz" data-testid="invite-tab-email" />
              </div>
              <div>
                <label className="hbt-label block mb-2">Also grants access to (optional)</label>
                <div className="text-[11px] text-neutral-500 mb-2">
                  Tick any additional companies this person should also manage — used when one contact covers multiple sister companies (e.g. group holdings).
                </div>
                <div className="border rounded-md p-3 max-h-[180px] overflow-y-auto space-y-1.5" style={{ borderColor: "var(--border)" }} data-testid="invite-extra-clients">
                  {allClients.filter((c) => c.id !== clientId).length === 0 && (
                    <div className="text-xs text-neutral-400">No other clients available.</div>
                  )}
                  {allClients.filter((c) => c.id !== clientId).map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:text-black" data-testid={`invite-extra-${c.id}`}>
                      <input
                        type="checkbox"
                        checked={extraClientIds.includes(c.id)}
                        onChange={() => toggleExtra(c.id)}
                      />
                      <span>{c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <button type="submit" disabled={inviting || !form.name || !form.email} className="btn-primary inline-flex items-center gap-2" data-testid="invite-tab-submit">
                <UserPlus className="w-4 h-4" /> {inviting ? "Sending…" : `Send invite${extraClientIds.length > 0 ? ` (${1 + extraClientIds.length} companies)` : ""}`}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
